/**
 * Admin Human Resource — people (`/api/admin/v2/hr`) and My workplace
 * (`/api/admin/v2/workplace`). Bodies are camelCase, matching
 * `src/client/types/hr`. `hr.view` reads, `hr.manage` writes; `/me` and
 * `/workplace/*` are open to any console user and only ever return the caller.
 */
import { useCallback } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useClientStore } from '@/client/stores/client';
import type { AdminEnvelope } from '@/client/types/admin/users-orgs';
import type {
  Employee,
  HrDepartment,
  HrLevel,
  HrMe,
  HrStaffAccount,
  LeaveType,
  OnboardingTask,
  TimeOffBalance,
  TimeOffRequest,
  WorkplaceApprovals,
  WorkplaceLeave,
  WorkplaceMe,
} from '@/client/types/hr';
import { DEFAULT_GC_TIME } from '@/client/constants';

export const HR_BASE = '/api/admin/v2/hr';
export const WORKPLACE_BASE = '/api/admin/v2/workplace';

export const hrKeys = {
  all: ['hr'] as const,
  me: () => [...hrKeys.all, 'me'] as const,
  employees: () => [...hrKeys.all, 'employees'] as const,
  employee: (id: string | null | undefined) => [...hrKeys.all, 'employee', id] as const,
  tasks: () => [...hrKeys.all, 'tasks'] as const,
  staffAccounts: () => [...hrKeys.all, 'staff-accounts'] as const,
  departments: () => [...hrKeys.all, 'departments'] as const,
  levels: () => [...hrKeys.all, 'levels'] as const,
  workplace: () => [...hrKeys.all, 'workplace'] as const,
  leaveTypes: () => [...hrKeys.all, 'leave-types'] as const,
  leaveRequests: (filters: Record<string, string | undefined>) => [...hrKeys.all, 'leave-requests', filters] as const,
  leaveBalances: (year: number) => [...hrKeys.all, 'leave-balances', year] as const,
  workplaceLeave: (year: number) => [...hrKeys.workplace(), 'leave', year] as const,
  workplaceApprovals: () => [...hrKeys.workplace(), 'approvals'] as const,
};

const qs = (params: Record<string, string | number | undefined | null>) => {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => v !== undefined && v !== null && v !== '' && q.set(k, String(v)));
  const out = q.toString();
  return out ? `?${out}` : '';
};

type HttpMethod = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

export function useHrApi(base: string = HR_BASE) {
  const request = useClientStore((s) => s.request);
  return useCallback(
    async <T,>(path: string, method: HttpMethod = 'GET', body?: unknown): Promise<T> => {
      const raw = await request<AdminEnvelope<T> | T>(`${base}${path}`, method, body);
      if (raw && typeof raw === 'object' && 'data' in raw) return (raw as AdminEnvelope<T>).data;
      return raw as T;
    },
    [request, base],
  );
}

export const useHrMeQuery = () => {
  const api = useHrApi();
  return useQuery({
    queryKey: hrKeys.me(),
    queryFn: () => api<HrMe>('/me'),
    staleTime: 30_000,
    gcTime: DEFAULT_GC_TIME,
  });
};

export const useHrEmployeesQuery = (enabled = true) => {
  const api = useHrApi();
  return useQuery({
    queryKey: hrKeys.employees(),
    queryFn: async () => (await api<{ employees: Employee[] }>('/employees')).employees ?? [],
    enabled,
    staleTime: 30_000,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
  });
};

export const useHrEmployeeQuery = (id: string | null | undefined) => {
  const api = useHrApi();
  return useQuery({
    queryKey: hrKeys.employee(id),
    queryFn: () => api<Employee>(`/employees/${encodeURIComponent(id as string)}`),
    enabled: Boolean(id),
    staleTime: 30_000,
    gcTime: DEFAULT_GC_TIME,
  });
};

/** The employee record linked to a platform account, or null. Needs `hr.view`;
 *  a caller without it gets an error, which callers treat as "unknown". */
export const useHrEmployeeByUidQuery = (uid: number | null | undefined, enabled = true) => {
  const api = useHrApi();
  return useQuery({
    queryKey: [...hrKeys.employees(), { uid }] as const,
    queryFn: async () =>
      (await api<{ employees: Employee[] }>(`/employees?uid=${uid}`)).employees?.[0] ?? null,
    enabled: Boolean(uid) && enabled,
    staleTime: 30_000,
    gcTime: DEFAULT_GC_TIME,
    retry: false,
  });
};

export const useHrTasksQuery = (enabled = true) => {
  const api = useHrApi();
  return useQuery({
    queryKey: hrKeys.tasks(),
    queryFn: async () => (await api<{ tasks: OnboardingTask[] }>('/tasks')).tasks ?? [],
    enabled,
    staleTime: 30_000,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
  });
};

export const useHrStaffAccountsQuery = (enabled = true) => {
  const api = useHrApi();
  return useQuery({
    queryKey: hrKeys.staffAccounts(),
    queryFn: async () => (await api<{ accounts: HrStaffAccount[] }>('/staff-accounts')).accounts ?? [],
    enabled,
    staleTime: 10_000,
    gcTime: DEFAULT_GC_TIME,
  });
};

/** Create (no `id`) or update (with `id`). A create needs `uid`: every employee has a FeelyFeely account. */
export const useUpsertEmployeeMutation = () => {
  const api = useHrApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...fields }: Partial<Employee> & { id?: string }) =>
      id
        ? api<Employee>(`/employees/${encodeURIComponent(id)}`, 'PATCH', fields)
        : api<Employee>('/employees', 'POST', fields),
    onSuccess: () => void qc.invalidateQueries({ queryKey: hrKeys.all }),
  });
};

export const useDeleteEmployeeMutation = () => {
  const api = useHrApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<{ id: string }>(`/employees/${encodeURIComponent(id)}`, 'DELETE'),
    onSuccess: () => void qc.invalidateQueries({ queryKey: hrKeys.all }),
  });
};

export const useImportStaffMutation = () => {
  const api = useHrApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (uids: number[]) =>
      api<{ created: Employee[]; skipped: number[] }>('/employees/import', 'POST', { uids }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: hrKeys.all }),
  });
};

export const useUpsertTaskMutation = () => {
  const api = useHrApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...fields }: Partial<OnboardingTask> & { id?: string }) =>
      id
        ? api<OnboardingTask>(`/tasks/${encodeURIComponent(id)}`, 'PATCH', fields)
        : api<OnboardingTask>('/tasks', 'POST', fields),
    onSuccess: () => void qc.invalidateQueries({ queryKey: hrKeys.all }),
  });
};

export const useDeleteTaskMutation = () => {
  const api = useHrApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<{ id: string }>(`/tasks/${encodeURIComponent(id)}`, 'DELETE'),
    onSuccess: () => void qc.invalidateQueries({ queryKey: hrKeys.all }),
  });
};

// ---------------------------------------------------------------- structure

export const useHrDepartmentsQuery = (enabled = true) => {
  const api = useHrApi();
  return useQuery({
    queryKey: hrKeys.departments(),
    queryFn: async () => (await api<{ departments: HrDepartment[] }>('/departments')).departments ?? [],
    enabled,
    staleTime: 60_000,
    gcTime: DEFAULT_GC_TIME,
  });
};

export const useUpsertDepartmentMutation = () => {
  const api = useHrApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...fields }: Partial<HrDepartment> & { id?: string }) =>
      id
        ? api<HrDepartment>(`/departments/${encodeURIComponent(id)}`, 'PATCH', fields)
        : api<HrDepartment>('/departments', 'POST', fields),
    onSuccess: () => void qc.invalidateQueries({ queryKey: hrKeys.all }),
  });
};

export const useDeleteDepartmentMutation = () => {
  const api = useHrApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<{ id: string }>(`/departments/${encodeURIComponent(id)}`, 'DELETE'),
    onSuccess: () => void qc.invalidateQueries({ queryKey: hrKeys.all }),
  });
};

export const useHrLevelsQuery = (enabled = true) => {
  const api = useHrApi();
  return useQuery({
    queryKey: hrKeys.levels(),
    queryFn: async () => (await api<{ levels: HrLevel[] }>('/levels')).levels ?? [],
    enabled,
    staleTime: 60_000,
    gcTime: DEFAULT_GC_TIME,
  });
};

export const useUpsertLevelMutation = () => {
  const api = useHrApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...fields }: Partial<HrLevel> & { id?: string }) =>
      id
        ? api<HrLevel>(`/levels/${encodeURIComponent(id)}`, 'PATCH', fields)
        : api<HrLevel>('/levels', 'POST', fields),
    onSuccess: () => void qc.invalidateQueries({ queryKey: hrKeys.all }),
  });
};

export const useDeleteLevelMutation = () => {
  const api = useHrApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<{ id: string }>(`/levels/${encodeURIComponent(id)}`, 'DELETE'),
    onSuccess: () => void qc.invalidateQueries({ queryKey: hrKeys.all }),
  });
};

// ----------------------------------------------------------- My workplace

export const useWorkplaceMeQuery = () => {
  const api = useHrApi(WORKPLACE_BASE);
  return useQuery({
    queryKey: hrKeys.workplace(),
    queryFn: () => api<WorkplaceMe>('/me'),
    staleTime: 30_000,
    gcTime: DEFAULT_GC_TIME,
  });
};

// ------------------------------------------------------------------ time off

export const useLeaveTypesQuery = (enabled = true) => {
  const api = useHrApi();
  return useQuery({
    queryKey: hrKeys.leaveTypes(),
    queryFn: async () => (await api<{ types: LeaveType[] }>('/leave-types')).types ?? [],
    enabled,
    staleTime: 60_000,
    gcTime: DEFAULT_GC_TIME,
  });
};

export const useUpsertLeaveTypeMutation = () => {
  const api = useHrApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...fields }: Partial<LeaveType> & { id?: string }) =>
      id
        ? api<LeaveType>(`/leave-types/${encodeURIComponent(id)}`, 'PATCH', fields)
        : api<LeaveType>('/leave-types', 'POST', fields),
    onSuccess: () => void qc.invalidateQueries({ queryKey: hrKeys.all }),
  });
};

/** `from`/`to` (YYYY-MM-DD) keep requests that overlap the window. */
export const useLeaveRequestsQuery = (
  filters: { status?: string; employeeId?: string; from?: string; to?: string } = {},
  enabled = true,
) => {
  const api = useHrApi();
  return useQuery({
    queryKey: hrKeys.leaveRequests(filters),
    queryFn: async () => (await api<{ requests: TimeOffRequest[] }>(`/leave-requests${qs(filters)}`)).requests ?? [],
    enabled,
    staleTime: 15_000,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
  });
};

export const useLeaveBalancesQuery = (year: number, enabled = true) => {
  const api = useHrApi();
  return useQuery({
    queryKey: hrKeys.leaveBalances(year),
    queryFn: async () => (await api<{ balances: TimeOffBalance[] }>(`/leave-balances${qs({ year })}`)).balances ?? [],
    enabled,
    staleTime: 15_000,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
  });
};

export type LeaveRequestInput = { typeId: string; startDate: string; endDate: string; reason?: string };

/** HR files a request on someone's behalf. It still waits for a decision. */
export const useFileLeaveRequestMutation = () => {
  const api = useHrApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: LeaveRequestInput & { employeeId: string }) => api<TimeOffRequest>('/leave-requests', 'POST', body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: hrKeys.all }),
  });
};

export type LeaveDecision = { id: string; decision: 'approve' | 'deny'; note?: string };

/** HR door — needs `hr.leave.approve`. */
export const useDecideLeaveMutation = () => {
  const api = useHrApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: LeaveDecision) =>
      api<TimeOffRequest>(`/leave-requests/${encodeURIComponent(id)}/decide`, 'POST', body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: hrKeys.all }),
  });
};

// My workplace — always the caller.

export const useWorkplaceLeaveQuery = (year: number) => {
  const api = useHrApi(WORKPLACE_BASE);
  return useQuery({
    queryKey: hrKeys.workplaceLeave(year),
    queryFn: () => api<WorkplaceLeave>(`/leave${qs({ year })}`),
    staleTime: 15_000,
    gcTime: DEFAULT_GC_TIME,
  });
};

export const useRequestMyLeaveMutation = () => {
  const api = useHrApi(WORKPLACE_BASE);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: LeaveRequestInput) => api<TimeOffRequest>('/leave', 'POST', body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: hrKeys.all }),
  });
};

export const useCancelMyLeaveMutation = () => {
  const api = useHrApi(WORKPLACE_BASE);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<TimeOffRequest>(`/leave/${encodeURIComponent(id)}/cancel`, 'POST'),
    onSuccess: () => void qc.invalidateQueries({ queryKey: hrKeys.all }),
  });
};

export const useWorkplaceApprovalsQuery = (enabled = true) => {
  const api = useHrApi(WORKPLACE_BASE);
  return useQuery({
    queryKey: hrKeys.workplaceApprovals(),
    queryFn: () => api<WorkplaceApprovals>('/approvals'),
    enabled,
    staleTime: 15_000,
    gcTime: DEFAULT_GC_TIME,
  });
};

/** Manager door — only for the caller's direct reports. */
export const useManagerDecideLeaveMutation = () => {
  const api = useHrApi(WORKPLACE_BASE);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: LeaveDecision) =>
      api<TimeOffRequest>(`/approvals/${encodeURIComponent(id)}/decide`, 'POST', body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: hrKeys.all }),
  });
};
